import { useDispatch, useSelector } from 'react-redux';

import {
    ProjectId,
    ProjectRef,
    TaskState,
    TaskStateObject,
    WorkspaceId,
} from 'core/model';
import { AppDispatch } from 'store';
import { hideCompletedTasksAction } from 'store/tasksThunks';
import {
    selectFilteredProjects,
    selectWorkspaceData,
    WorkspaceState,
} from 'store/workspaceSlice';
import { selectLayout } from 'store/layoutSlice';

import Button from 'layout/atoms/Button';
import Container from 'layout/atoms/Container';
import Grid from 'layout/atoms/Grid';
import TaskExecute from 'layout/atoms/TaskExecute';
import PlannedTasksList from 'layout/organisms/PlannedTasksList/PlannedTasksList';

import EmptyState from './EmptyState';
import TaskQuickAdd from './TaskQuickAdd';

export type PlannedTaskOrigin = 'workspace' | 'project' | 'automation';

export type PlannedTask = {
    task: TaskStateObject;
    color?: string | null;
    projectName?: string;
    projectRef: ProjectRef;
    origin: PlannedTaskOrigin;
};

const ExecutePage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const workspaceData: WorkspaceState = useSelector(selectWorkspaceData);
    const filteredProjects = useSelector(selectFilteredProjects);
    const layoutState = useSelector(selectLayout);
    const searchQuery = layoutState.searchQuery.toLowerCase();

    // Filter workspace tasks based on search query
    const filteredWorkspaceTasks = searchQuery
        ? workspaceData.tasks.filter((t) =>
              t.name.toLowerCase().includes(searchQuery)
          )
        : workspaceData.tasks;

    const activeTasks: PlannedTask[] = [
        ...filteredWorkspaceTasks
            .filter(
                (t) => t.done || workspaceData.plannedTasks.indexOf(t.id) >= 0
            )
            .map((task) => {
                return {
                    task,
                    projectRef: {
                        workspaceId: workspaceData.id as WorkspaceId,
                        projectId: workspaceData.id as ProjectId,
                    },
                    origin: 'workspace' as PlannedTaskOrigin,
                };
            }),
        ...filteredProjects.flatMap((p) => {
            return p.tasks
                .filter(
                    (t: any) =>
                        t.done || workspaceData.plannedTasks.indexOf(t.id) >= 0
                )
                .map((task: any) => {
                    return {
                        task,
                        color: p.color,
                        projectName: p.name,
                        projectRef: {
                            workspaceId: p.workspaceId as WorkspaceId,
                            projectId: p.id,
                        },
                        origin: 'project' as PlannedTaskOrigin,
                    };
                });
        }),
        ...workspaceData.automations.map((a) => {
            return {
                task: {
                    id: a.id,
                    name: a.name,
                    planned: true,
                    state: 'PLANNED' as TaskState,
                    createdAt: new Date().toISOString(),
                    completedAt: a.completedAt,
                    done: false,
                },
                projectRef: {
                    workspaceId: workspaceData.id as WorkspaceId,
                    projectId: a.id as ProjectId,
                },
                origin: 'automation' as PlannedTaskOrigin,
            };
        }),
    ].sort(
        (t1, t2) =>
            workspaceData.plannedTasks.indexOf(t1.task.id) -
            workspaceData.plannedTasks.indexOf(t2.task.id)
    );

    const partitionedTasks = activeTasks.reduce(
        (acc: [PlannedTask[], PlannedTask[]], plannedTask) => {
            // Only include non-automation tasks in completed tasks
            if (plannedTask.origin === 'automation') {
                if (plannedTask.task.completedAt === null) {
                    acc[0].push(plannedTask);
                }
                // Ignore completed automations
            } else {
                acc[plannedTask.task.completedAt === null ? 0 : 1].push(
                    plannedTask
                );
            }
            return acc;
        },
        [[], []]
    );
    const _plannedTasks: PlannedTask[] = partitionedTasks[0];
    const completedTasks: PlannedTask[] = partitionedTasks[1];
    const displaySeparator =
        _plannedTasks.length > 0 && completedTasks.length > 0;

    const hideCompletedTasks = () => {
        dispatch(hideCompletedTasksAction());
    };

    return (
        <>
            <TaskQuickAdd plannedTasksLength={_plannedTasks.length} />
            {_plannedTasks.length === 0 && completedTasks.length === 0 && (
                <EmptyState />
            )}
            {_plannedTasks.length > 0 && <PlannedTasksList />}
            <Container size="s" marginTop="16">
                {completedTasks.length > 0 && (
                    <>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '16px',
                                paddingTop: displaySeparator ? '24px' : '16px',
                                borderTop: displaySeparator
                                    ? '1px solid var(--grey400)'
                                    : 'none',
                            }}
                        >
                            <h3
                                style={{
                                    margin: 0,
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: 'var(--text)',
                                    opacity: 0.8,
                                }}
                            >
                                Completed tasks ({completedTasks.length})
                            </h3>
                            <Button
                                theme="plain"
                                size="s"
                                onClick={hideCompletedTasks}
                                style={{
                                    opacity: 0.7,
                                    fontSize: '13px',
                                }}
                            >
                                Hide completed
                            </Button>
                        </div>
                        <Grid columns={1} gap={8}>
                            {completedTasks
                                .sort((t1, t2) =>
                                    t2.task.state.localeCompare(t1.task.state)
                                )
                                .map((task) => (
                                    <TaskExecute
                                        key={task.task.id}
                                        id={task.task.id}
                                        state="completed"
                                    />
                                ))}
                        </Grid>
                    </>
                )}
            </Container>
        </>
    );
};

export default ExecutePage;
